import type { IngestionRunResult } from './types'

const ENTITY_LABELS: Record<string, string> = {
  client: 'Clients',
  appointment: 'Appointments',
  provider: 'Providers',
  visit: 'Visits',
  visit_codes: 'Visit codes',
  care_gap: 'Care gaps',
  order: 'Orders',
  prescription_order: 'Prescription orders',
  referral_order: 'Referral orders',
  timeslot: 'Timeslots',
  cob_claim: 'COB claims',
}

const SOURCE_LABELS: Record<string, string> = {
  cobalt: 'Cobalt',
  ecw: 'eClinicalWorks',
  smart_care: 'SmartCare',
  epic_csv: 'Epic',
  khs: 'Kern Health Systems',
  bcbsmn: 'Blue Cross Blue Shield of Minnesota',
  ccah: 'Central California Alliance for Health',
  clarity: 'Clarity',
  php: 'PHP',
  safety_net: 'Safety Net',
  wpc: 'WPC',
}

const STATUS_LABELS: Record<string, string> = {
  queued: 'Queued',
  dispatching: 'Running',
  running: 'Running',
  completed_with_errors: 'Completed with errors',
  cancelled: 'Cancelled',
  pending: 'Pending',
  pending_mapping: 'Pending mapping',
  generating_mapping: 'Generating mapping',
  awaiting_mapping_review: 'Awaiting mapping review',
  processing: 'Processing',
  completed: 'Completed',
  error: 'Error',
  failed: 'Failed',
  skip: 'Skipped',
}

const UNKNOWN_SOURCE_LABEL = 'Unknown source'
const COBALT_ENTITIES = new Set([
  'patients',
  'providers',
  'facilities',
  'appointments',
  'referrals',
  'orders',
  'availability',
])

/**
 * Humanizes an unmapped raw key: splits on underscores/hyphens and capitalizes
 * the first word, so an unknown value renders as readable text instead of a
 * blank or a raw enum token.
 */
export function humanizeKey(raw: string): string {
  const words = raw
    .trim()
    .split(/[_\-\s]+/)
    .filter(Boolean)
  if (words.length === 0) return raw
  const joined = words.join(' ')
  return joined.charAt(0).toUpperCase() + joined.slice(1)
}

/** Maps a raw entity to a customer-facing label, humanizing unknown values. */
export function translateEntity(entity: string | null | undefined): string {
  if (!entity) return 'Unknown data type'
  return ENTITY_LABELS[entity] ?? humanizeKey(entity)
}

/** Relay keeps sync and file entity keys separate; qualify sync filter options. */
export function translateEntityFilter(entity: string): string {
  const label = translateEntity(entity)
  return COBALT_ENTITIES.has(entity) ? `${label} (Cobalt)` : label
}

/** Maps a raw source to a readable vendor name, humanizing unknown values. */
export function translateSource(source: string | null | undefined): string {
  if (!source) return UNKNOWN_SOURCE_LABEL
  return SOURCE_LABELS[source] ?? humanizeKey(source)
}

/** Include record errors in completed outcomes, humanizing unknown statuses. */
export function translateStatus(
  status: string | null | undefined,
  result?: IngestionRunResult | null
): string {
  if (!status) return 'Unknown'
  // Relay supplies Cobalt outcomes. File runs retain "completed" even when
  // their summary includes rejected rows or system errors.
  const hasErrors = [
    result?.rejected_source_record_count,
    result?.system_error_source_record_count,
  ].some((count) => (count ?? 0) > 0)
  const outcome =
    status === 'completed' && hasErrors ? 'completed_with_errors' : status
  return STATUS_LABELS[outcome] ?? humanizeKey(outcome)
}

export interface TranslatedOption {
  label: string
  value: string
}

/**
 * Builds alphabetized filter options from a list of raw values, translating
 * each with the given translator. Callers pass the signed-in org's own
 * distinct values (from the facets endpoint) so the options never expose
 * sources or data types the organization doesn't ingest.
 */
export function toTranslatedOptions(
  values: string[],
  translate: (value: string) => string
): TranslatedOption[] {
  return values
    .map((value) => ({ label: translate(value), value }))
    .toSorted((a, b) => a.label.localeCompare(b.label))
}
